/**
 * Timestamp formatting helpers used throughout the UI.
 *
 * All times are rendered in **24-hour format** under the `uk-UA` locale:
 * `DD.MM.YYYY HH:mm(:ss)` — the format operators in this deployment expect,
 * regardless of the browser's language preference. Dates display in the
 * user's local timezone — never UTC — because operators reason about
 * measurement times in local wall-clock.
 *
 * Pass either an ISO-8601 string or a Date object.
 */
const DATE_TIME_FORMATTER = new Intl.DateTimeFormat('uk-UA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
})

const DATE_FORMATTER = new Intl.DateTimeFormat('uk-UA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

const TIME_FORMATTER = new Intl.DateTimeFormat('uk-UA', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
})

const TIME_HHMM_FORMATTER = new Intl.DateTimeFormat('uk-UA', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

const DATE_TIME_SHORT_FORMATTER = new Intl.DateTimeFormat('uk-UA', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

function toDate(v: string | Date): Date {
  return v instanceof Date ? v : new Date(v)
}

export function formatDateTime(value: string | Date): string {
  return DATE_TIME_FORMATTER.format(toDate(value))
}

export function formatDate(value: string | Date): string {
  return DATE_FORMATTER.format(toDate(value))
}

export function formatTime(value: string | Date): string {
  return TIME_FORMATTER.format(toDate(value))
}

/** HH:mm (24-hour, en-GB). Used in chart ticks and the paper-form Time field. */
export function formatTimeHHMM(value: string | Date): string {
  return TIME_HHMM_FORMATTER.format(toDate(value))
}

/** "13 Apr, 14:30" — compact absolute timestamp for ranges and captions. */
export function formatDateTimeShort(value: string | Date): string {
  return DATE_TIME_SHORT_FORMATTER.format(toDate(value))
}

/**
 * Last 4 digits of a patient external ID (e.g. service number, MRN). Falls
 * back to the full ID when it has fewer than four digits. Returns undefined
 * when no ID is available.
 */
export function formatPatientLast4(externalId?: string): string | undefined {
  if (!externalId) return undefined
  const digits = externalId.replace(/\D/g, '')
  if (digits.length < 4) return externalId
  return digits.slice(-4)
}

/**
 * Patient display label for list views. Shows the real name only to admins
 * with a meaningful name on file; everyone else (and admins viewing rows
 * stamped with the `Unknown` placeholder) sees the externalId's last 4
 * digits. Falls back to an em-dash when neither is available.
 *
 * Non-admins receive a server-masked `name` of `"XXXX"` and an externalId
 * of `"XXXX<digits>"`, so this helper produces the right answer either way.
 */
export function displayPatientName(
  patient: { name?: string; externalId?: string },
  isAdmin: boolean,
): string {
  const hasName = patient.name && patient.name !== 'Unknown'
  if (isAdmin && hasName) return patient.name!
  return formatPatientLast4(patient.externalId) ?? '—'
}
